import * as React from 'react';
import { WorkspacesView, WorkspacesViewProps } from './Workspaces.view';
import { SettingsTeamMember, SettingsWorkspace } from '../../../models/settings-pages';
import { GetOrganizationLazyQueryHookResult, UserOrganizationRole } from '../../../models/graphql';
import { useEffect, useMemo, useState } from 'react';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import {
  mapOrganizationInvitationsToTeamMembersType,
  mapOrganizationUsersToTeamMembersType,
  addWorkspacesToWorkspaceType,
  sortArrayOfTeamMembers
} from '../../../helpers/teamMembers';
import { useUserData } from '../../../hooks/useUserData';

export type WorkspacesControllerProps = Omit<
  WorkspacesViewProps,
  | 'workspaces'
  | 'reachedWorkspaceLimit'
  | 'openNewWorkspaceModal'
  | 'setOpenNewWorkspaceModal'
  | 'allTeamMembers'
  | 'modalWorkspace'
  | 'openEditWorkspaceModal'
  | 'openAddWorkspaceModal'
  | 'openDeleteWorkspaceModal'
  | 'setOpenDeleteWorkspaceModal'
  | 'openWorkspaceDeleteModal'
  | 'openDeleteWorkspaceWarningModal'
  | 'setOpenDeleteWorkspaceWarningModal'
> & {
  getOrganization: GetOrganizationLazyQueryHookResult[0];
};

export const WorkspacesController = (props: WorkspacesControllerProps): JSX.Element => {
  const { userData } = useUserData();
  const { selectedOrganization } = useSelectedOrganization();
  const [openNewWorkspaceModal, setOpenNewWorkspaceModal] = useState<boolean>(false);
  const [openDeleteWorkspaceModal, setOpenDeleteWorkspaceModal] = useState<boolean>(false);
  const [openDeleteWorkspaceWarningModal, setOpenDeleteWorkspaceWarningModal] = useState<boolean>(false);
  const [modalWorkspace, setModalWorkspace] = useState<SettingsWorkspace | null>(null);

  const openAddWorkspaceModal = () => {
    setModalWorkspace(null);
    setOpenNewWorkspaceModal(true);
  };

  const openEditWorkspaceModal = (workspace: SettingsWorkspace) => {
    setModalWorkspace(workspace);
    setOpenNewWorkspaceModal(true);
  };

  const openWorkspaceDeleteModal = (workspace: SettingsWorkspace) => {
    setModalWorkspace(workspace);
    setOpenDeleteWorkspaceModal(true);
  };

  useEffect(() => {
    selectedOrganization &&
      props.getOrganization({
        variables: { organizationId: selectedOrganization.id }
      });
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [selectedOrganization]);

  const organization = props.organizationResults.data?.organization;
  let teamMembers: SettingsTeamMember[] = [];
  // TS complains unless we wrap in useMemo (or tell to ignore)
  let workspaces: SettingsWorkspace[] = useMemo(() => [], []);
  let sortedUserAssignmentsAndInvites = undefined;

  if (organization) {
    teamMembers = [
      ...mapOrganizationInvitationsToTeamMembersType(organization),
      ...mapOrganizationUsersToTeamMembersType(organization)
    ];
    sortedUserAssignmentsAndInvites = sortArrayOfTeamMembers(teamMembers);
    workspaces = addWorkspacesToWorkspaceType(organization);
  }

  const maxWorkspaces = 10;

  // use for...of loop, so we can break out if/when found
  const reachedWorkspaceLimit = useMemo(() => {
    let count = 0;
    for (const workspace of workspaces) {
      for (const member of workspace.members) {
        if (member.id === userData?.id && member.role === UserOrganizationRole.OWNER) {
          count++;
          if (count >= maxWorkspaces) {
            return true;
          }
        }
      }
    }

    return false;
  }, [userData, workspaces, maxWorkspaces]);

  const viewProps: WorkspacesViewProps = {
    ...props,
    workspaces,
    reachedWorkspaceLimit,
    openNewWorkspaceModal,
    setOpenNewWorkspaceModal,
    modalWorkspace,
    openEditWorkspaceModal,
    openAddWorkspaceModal,
    openDeleteWorkspaceModal,
    setOpenDeleteWorkspaceModal,
    openWorkspaceDeleteModal,
    openDeleteWorkspaceWarningModal,
    setOpenDeleteWorkspaceWarningModal,
    allTeamMembers: sortedUserAssignmentsAndInvites
  };

  return <WorkspacesView {...viewProps} />;
};
